import { memo, useRef } from 'react'
import { trackInternalEvent } from '@rio/tracking'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import { useI18n } from 'builder/hooks/useI18n'
import { useEffectOnMount } from 'builder/hooks/useEffectOnMount'
import {
  AIResumeDraftEvents,
  PrefillResumeEvents,
  UseExampleEvents,
} from 'builder/modules/constants'
import { PrefillResumeOptions } from 'builder/modules/resumeEditor'
import { MouseClickEvent } from 'builder/modules/ui'
import ModalOverlay from '../ModalOverlay'
import Tooltip from '../Tooltip'
import {
  OptionsContainer,
  CloseIconWrapper,
  CloseIcon,
  Title,
  SubTitle,
  Header,
} from './OptionsViewV3/styles'
import {
  CardContainer,
  Card,
  CardTitleWrapper,
  CardTitle,
  CreateWithAICard,
  IconWrapper,
  CreateWithAIArrowIcon,
  ImageWrapper,
  Image,
  CreateWithAICardTitle,
  CardTitleContainer,
  AIPoweredBadge,
  AIStarImg,
  ArrowIcon,
} from './OptionsViewV3/OptionCards/styles'
import createNewResumeImage from './OptionsViewV3/assets/create-new-resume.svg'
import createWithAiImage from './OptionsViewV3/assets/create-with-ai.svg'
import starImg from './OptionsViewV3/assets/star.svg'
import createFromExampleImage from './OptionsViewV3/assets/create-from-example.svg'
import uploadResumeImage from './OptionsViewV3/assets/upload-resume.svg'
import createWithLinkedinImage from './OptionsViewV3/assets/create-with-linkedin.svg'
import { ALLOWED_FILES } from './constants'
import { FileInput } from './UploadResume/styles'

type Props = {
  handleClose: () => void
  handleDisabledItemClick: (type: PrefillResumeOptions, file?: File) => void
}

/**
 * Version of FillResumeModal for unauthorized builder experiment
 */

export const FillResumeModalGuest = memo((props: Props) => {
  const { handleClose, handleDisabledItemClick } = props
  const { isPhone } = useMediaQueries()
  const { i18n } = useI18n()
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffectOnMount(() => {
    trackInternalEvent(PrefillResumeEvents.seeStartScreen)
  })

  const handleCreteNewResume = () => {
    trackInternalEvent(PrefillResumeEvents.clickCreateResume)
    handleClose()
  }

  const handleFileInputClick = (event: MouseClickEvent) => {
    event.stopPropagation()
  }

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedResumeFile = event.target.files?.[0]

    if (selectedResumeFile) {
      handleDisabledItemClick(PrefillResumeOptions.uploadResume, selectedResumeFile)
    }
  }

  const renderCreateNewResume = () => {
    const title = i18n.t('builder.prefill_resume.create_resume')

    return (
      <Card onClick={handleCreteNewResume}>
        <ImageWrapper>
          <Image src={createNewResumeImage} alt="Create New Resume" />
        </ImageWrapper>
        <CardTitleWrapper>
          <CardTitle>{title}</CardTitle>
        </CardTitleWrapper>
        <IconWrapper>
          <ArrowIcon />
        </IconWrapper>
      </Card>
    )
  }

  const renderCreateWithAI = () => {
    return (
      <CreateWithAICard
        onClick={() => {
          trackInternalEvent(AIResumeDraftEvents.clickCreateWithAI)
          handleDisabledItemClick(PrefillResumeOptions.createWithAi)
        }}
      >
        <ImageWrapper>
          <Image src={createWithAiImage} alt="Create with AI" />
        </ImageWrapper>
        <CardTitleWrapper>
          <CardTitleContainer>
            <CreateWithAICardTitle>
              {i18n.t('builder.ai_resume_draft.create_with_ai')}
            </CreateWithAICardTitle>
            <Tooltip value={i18n.t('builder.ai_resume_draft.ai_powered')}>
              <AIPoweredBadge>
                <AIStarImg src={starImg} alt="AI star" />
              </AIPoweredBadge>
            </Tooltip>
          </CardTitleContainer>
        </CardTitleWrapper>
        <CreateWithAIArrowIcon>
          <ArrowIcon />
        </CreateWithAIArrowIcon>
      </CreateWithAICard>
    )
  }

  const renderUseExample = () => {
    return (
      <Card
        onClick={() => {
          trackInternalEvent(UseExampleEvents.clickUseAnExampleStartScreen)
          handleDisabledItemClick(PrefillResumeOptions.useExample)
        }}
      >
        <ImageWrapper>
          <Image src={createFromExampleImage} alt="Use resume example" />
        </ImageWrapper>
        <CardTitleWrapper>
          <CardTitle>{i18n.t('builder.use_example.option.heading')}</CardTitle>
        </CardTitleWrapper>
        <IconWrapper>
          <ArrowIcon />
        </IconWrapper>
      </Card>
    )
  }

  const renderUploadResume = () => {
    return (
      <Card
        onClick={() => {
          trackInternalEvent(PrefillResumeEvents.clickUploadResume)
          fileInputRef.current?.click()
        }}
      >
        <ImageWrapper>
          <Image src={uploadResumeImage} alt="Upload Resume" />
        </ImageWrapper>
        <CardTitleWrapper>
          <CardTitle>{i18n.t('builder.prefill_resume.upload_resume')}</CardTitle>
        </CardTitleWrapper>
        <IconWrapper>
          <ArrowIcon />
        </IconWrapper>
      </Card>
    )
  }

  const renderCreateWithLinkedinProfile = () => {
    return (
      <Card
        onClick={() => {
          trackInternalEvent(PrefillResumeEvents.clickCreateWithLiStartScreen)
          handleDisabledItemClick(PrefillResumeOptions.createWithLinkedIn)
        }}
      >
        <ImageWrapper>
          <Image src={createWithLinkedinImage} alt="With Linkedin Profile" />
        </ImageWrapper>
        <CardTitleWrapper>
          <CardTitle>{i18n.t('builder.prefill_resume.create_with_linkedin')}</CardTitle>
        </CardTitleWrapper>
        <IconWrapper>
          <ArrowIcon />
        </IconWrapper>
      </Card>
    )
  }

  return (
    <ModalOverlay
      onClick={handleClose}
      overlayFadeDuration={150}
      contentSlideDuration={150}
      fullScreen={isPhone}
    >
      <OptionsContainer>
        <CloseIconWrapper
          onClick={() => {
            trackInternalEvent(PrefillResumeEvents.clickCrossButtonStartScreen)
            handleClose()
          }}
        >
          <CloseIcon />
        </CloseIconWrapper>
        <Header>
          <Title>{i18n.t('builder.prefill_resume.start_title')}</Title>
          <SubTitle>{i18n.t('builder.prefill_resume.start_subtitle')}</SubTitle>
        </Header>
        <CardContainer gridCount={4}>
          {renderCreateNewResume()}
          {renderCreateWithAI()}
          {renderUploadResume()}
          {renderCreateWithLinkedinProfile()}
          {renderUseExample()}
        </CardContainer>
        <FileInput
          type="file"
          ref={fileInputRef}
          onClick={handleFileInputClick}
          onChange={handleFileInputChange}
          accept={ALLOWED_FILES}
        />
      </OptionsContainer>
    </ModalOverlay>
  )
})
